import React, { useContext, useEffect, useReducer, useState } from "react";
import { Redirect } from "react-router-dom";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import SearchIcon from "@material-ui/icons/Search";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import IconButton from "@material-ui/core/IconButton";
import EditIcon from "@material-ui/icons/Edit";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import FormControl from "@material-ui/core/FormControl";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import CircularProgress from "@material-ui/core/CircularProgress";

import api from "../../services/api";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import MainContainer from "../../components/MainContainer";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from "../../translate/i18n";

const reducer = (state, action) => {
  if (action.type === "LOAD_PROJECTS") {
    const projects = action.payload;
    const newProjects = [];
    projects.forEach((project) => {
      const idx = state.findIndex((p) => p.id === project.id);
      if (idx !== -1) {
        state[idx] = project;
      } else {
        newProjects.push(project);
      }
    });
    return [...state, ...newProjects];
  }

  if (action.type === "UPSERT_PROJECT") {
    const project = action.payload;
    const idx = state.findIndex((p) => p.id === project.id);
    if (idx !== -1) {
      state[idx] = project;
      return [...state];
    }
    return [project, ...state];
  }

  if (action.type === "DELETE_PROJECT") {
    const projectId = action.payload;
    const idx = state.findIndex((p) => p.id === projectId);
    if (idx !== -1) state.splice(idx, 1);
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const emptyProjectForm = {
  name: "",
  company: "",
  description: "",
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(3),
    overflow: "auto",
    ...theme.scrollbarStyles,
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(1),
    },
  },
  filterBar: {
    display: "flex",
    gap: theme.spacing(2),
    flexWrap: "wrap",
    alignItems: "center",
    width: "100%",
    [theme.breakpoints.down("xs")]: {
      gap: theme.spacing(1),
    },
  },
  filterSearch: {
    minWidth: 220,
    [theme.breakpoints.down("xs")]: {
      width: "100%",
      minWidth: "100%",
    },
  },
  filterControl: {
    minWidth: 180,
    [theme.breakpoints.down("xs")]: {
      width: "100%",
      minWidth: "100%",
    },
  },
  actionButton: {
    whiteSpace: "nowrap",
    [theme.breakpoints.down("xs")]: {
      width: "100%",
    },
  },
  responsiveTable: {
    minWidth: 720,
  },
  formGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: theme.spacing(2),
    paddingTop: theme.spacing(1.5),
    [theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr" },
  },
  fullSpan: { gridColumn: "1 / -1" },
}));

const Projects = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  const [projects, dispatch] = useReducer(reducer, []);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);

  const [searchParam, setSearchParam] = useState("");
  const [companyFilter, setCompanyFilter] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [projectForm, setProjectForm] = useState(emptyProjectForm);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam, companyFilter]);

  useEffect(() => {
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get("/projects", {
          params: {
            searchParam,
            pageNumber,
            ...(companyFilter ? { company: companyFilter } : {}),
          },
        });
        dispatch({ type: "LOAD_PROJECTS", payload: data.projects || [] });
        setHasMore(data.hasMore);
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [searchParam, companyFilter, pageNumber]);

  if (user?.profile !== "epo") {
    return <Redirect to="/" />;
  }

  const companyOptions = Array.from(
    new Set(projects.map((project) => project.company).filter(Boolean))
  );

  const openCreate = () => {
    setEditingProject(null);
    setProjectForm(emptyProjectForm);
    setModalOpen(true);
  };

  const openEdit = (project) => {
    setEditingProject(project);
    setProjectForm({
      name: project.name || "",
      company: project.company || "",
      description: project.description || "",
    });
    setModalOpen(true);
  };

  const handleClose = () => {
    setEditingProject(null);
    setProjectForm(emptyProjectForm);
    setModalOpen(false);
  };

  const handleChange = (field) => (event) => {
    setProjectForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleSave = async () => {
    if (!projectForm.name.trim()) return;

    setSaving(true);
    try {
      if (editingProject) {
        const { data } = await api.put(`/projects/${editingProject.id}`, projectForm);
        dispatch({ type: "UPSERT_PROJECT", payload: data });
      } else {
        const { data } = await api.post("/projects", projectForm);
        dispatch({ type: "UPSERT_PROJECT", payload: data });
      }
      toast.success(i18n.t("projects.form.success"));
      handleClose();
    } catch (err) {
      toastError(err);
    }
    setSaving(false);
  };

  const handleDelete = async (projectId) => {
    if (!window.confirm(i18n.t("projects.confirmDelete"))) return;

    try {
      await api.delete(`/projects/${projectId}`);
      dispatch({ type: "DELETE_PROJECT", payload: Number(projectId) });
      toast.success(i18n.t("projects.deleteSuccess"));
    } catch (err) {
      toastError(err);
    }
  };

  const loadMore = () => setPageNumber((p) => p + 1);

  const handleScroll = (event) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    if (scrollHeight - (scrollTop + 120) < clientHeight) {
      loadMore();
    }
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>{i18n.t("projects.title")}</Title>
        <MainHeaderButtonsWrapper>
          <div className={classes.filterBar}>
            <TextField
              className={classes.filterSearch}
              placeholder={i18n.t("projects.searchPlaceholder")}
              type="search"
              value={searchParam}
              onChange={(event) => setSearchParam(event.target.value.toLowerCase())}
              variant="outlined"
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon style={{ color: "gray" }} />
                  </InputAdornment>
                ),
              }}
            />
            <FormControl variant="outlined" size="small" className={classes.filterControl}>
              <Select
                value={companyFilter}
                onChange={(event) => setCompanyFilter(event.target.value)}
                displayEmpty
              >
                <MenuItem value=""><em>{i18n.t("projects.filters.allCompanies")}</em></MenuItem>
                {companyOptions.map((company) => (
                  <MenuItem key={company} value={company}>{company}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button className={classes.actionButton} variant="contained" color="primary" onClick={openCreate}>
              {i18n.t("projects.buttons.add")}
            </Button>
          </div>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.mainPaper} onScroll={handleScroll}>
        <Table className={classes.responsiveTable}>
          <TableHead>
            <TableRow>
              <TableCell>{i18n.t("projects.table.name")}</TableCell>
              <TableCell>{i18n.t("projects.table.company")}</TableCell>
              <TableCell>{i18n.t("projects.table.contacts")}</TableCell>
              <TableCell>{i18n.t("projects.table.description")}</TableCell>
              <TableCell align="center">{i18n.t("projects.table.actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {projects.map((project) => (
              <TableRow key={project.id}>
                <TableCell>{project.name}</TableCell>
                <TableCell>{project.company || "-"}</TableCell>
                <TableCell>{(project.contacts || []).length}</TableCell>
                <TableCell>{project.description || "-"}</TableCell>
                <TableCell align="center">
                  <IconButton size="small" onClick={() => openEdit(project)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleDelete(project.id)}>
                    <DeleteOutlineIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {loading && <TableRowSkeleton avatar={false} columns={4} />}
          </TableBody>
        </Table>
      </Paper>

      <Dialog open={modalOpen} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingProject
            ? i18n.t("projects.form.title.edit")
            : i18n.t("projects.form.title.add")}
        </DialogTitle>
        <DialogContent dividers>
          <div className={classes.formGrid}>
            <TextField
              label={i18n.t("projects.form.name")}
              value={projectForm.name}
              onChange={handleChange("name")}
              variant="outlined"
              size="small"
              fullWidth
              required
            />
            <TextField
              label={i18n.t("projects.form.company")}
              value={projectForm.company}
              onChange={handleChange("company")}
              variant="outlined"
              size="small"
              fullWidth
            />
            <TextField
              className={classes.fullSpan}
              label={i18n.t("projects.form.description")}
              value={projectForm.description}
              onChange={handleChange("description")}
              variant="outlined"
              size="small"
              multiline
              rows={3}
              fullWidth
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={saving}>
            {i18n.t("projects.form.buttons.cancel")}
          </Button>
          <Button
            onClick={handleSave}
            color="primary"
            variant="contained"
            disabled={saving || !projectForm.name.trim()}
          >
            {saving ? <CircularProgress size={20} color="inherit" /> : i18n.t("projects.form.buttons.save")}
          </Button>
        </DialogActions>
      </Dialog>
    </MainContainer>
  );
};

export default Projects;
